import { Datagrid, DateField, Filter, NumberField, ReferenceArrayInput, ReferenceField, ReferenceInput, SelectArrayInput, SelectInput, TextField, useListContext,
  AutocompleteInput,} from 'react-admin';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';

import { Typography } from '@mui/material';
import moment from 'moment';
import {
  Permission,
  ReportList,
  RequiredChain
} from '../../components';
import { BetweenDatesTimesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import { filterToQuery } from '../../utils';

const titleSx = {
  textAlign: 'center',
  fontWeight: 'bold',
  marginTop: '20px',
  marginBottom: '10px',
};

const ScoringReportContent = () => {
  const { data, filterValues } = useListContext();

  if (!filterValues?.scoringId) {
    return <Typography align="center" sx={{ py: 4 }}>Selecione uma regra de pontuação para visualizar o relatório.</Typography>;
  }

  if (!data || data.length === 0) {
    return <Typography align="center" sx={{ py: 4 }}>Nenhum dado encontrado para os filtros selecionados.</Typography>;
  }

  const info = data[0];
  const { summaryData, byFuel, customers, total } = info;

  return (
    <>
      <Typography sx={titleSx}>Resumo Geral</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginBottom: 30 }} data={[summaryData]} total={1}>
        <NumberField
          source="totalPoints"
          label="Total de Pontos"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField source="totalFillins" label="Abastecimentos" textAlign="center" sortable={false} />
        <NumberField source="totalCustomers" label="Clientes" textAlign="center" sortable={false} />
        <NumberField
          source="totalAmount"
          label="Total em Litros"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField
          source="totalValue"
          label="Total em R$"
          textAlign="center"
          sortable={false}
          options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField
          source="averageTicket"
          label="Ticket Médio"
          textAlign="center"
          sortable={false}
          options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
      </Datagrid>

      <Typography sx={titleSx}>Totais por Combustível</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginBottom: 30 }} data={byFuel} total={1}>
        <TextField source="fuelName" label="Combustível" sortable={false} />
        <NumberField
          source="factor"
          label="Fator"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField source="count" label="Abastecimentos" textAlign="center" sortable={false} />
        <NumberField
          source="amount"
          label="Litros"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField
          source="points"
          label="Pontos"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
      </Datagrid>

      <Typography sx={titleSx}>Lista de Clientes ({total})</Typography>
      <Datagrid data={customers} rowClick={false} bulkActionButtons={false} total={1}>
        <TextField source="cpf" label="CPF" sortable={false} />
        <TextField source="name" label="Nome" sortable={false} />
        <NumberField source="fillins" label="Abastecimentos" textAlign="center" sortable={false} />
        <NumberField
          source="amount"
          label="Litros"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField
          source="value"
          label="Valor R$"
          textAlign="center"
          sortable={false}
          options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <NumberField
          source="points"
          label="Pontos"
          textAlign="center"
          sortable={false}
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <DateField source="lastFillin" label="Último Abastecimento" textAlign="center" sortable={false} locales="pt-BR" showTime />
      </Datagrid>
    </>
  );
};

const ListFilters = () => {
  const { filterValues } = useListContext();
  return (
    <Filter>
      <BetweenDatesTimesInput alwaysOn />
      {Permission({
        permission: PERMISSIONS.ADMIN,
        children: (
          <ReferenceInput
            source="chainId"
            reference="chains"
            sort={{ field: 'name', order: 'ASC' }}
            // @ts-ignore
            perPage={null}
            alwaysOn
          >
            <AutocompleteInput filterToQuery={filterToQuery} label="Rede" optionText="name" />
          </ReferenceInput>
        )
      })}
      <ReferenceInput
        source="scoringId"
        reference="scorings"
        filter={{ chainId: filterValues.chainId || getChainId() }}
        sort={{ field: 'identify', order: 'ASC' }}
        // @ts-ignore
        perPage={null}
        alwaysOn
      >
        <SelectInput label="Regra de Pontuação" optionText="identify" />
      </ReferenceInput>
      {!getPlaceId() && (
        <ReferenceArrayInput
          source="stationIds"
          reference="stations"
          filter={{ chainId: filterValues.chainId || getChainId() }}
          sort={{ field: 'name', order: 'ASC' }}
          // @ts-ignore
          perPage={null}
          alwaysOn
        >
          <SelectArrayInput label="Postos" optionText="name" />
        </ReferenceArrayInput>
      )}
      <ReferenceArrayInput
        source="fuelIds"
        reference="fuels"
        filter={{ chainId: filterValues.chainId || getChainId() }}
        sort={{ field: 'name', order: 'ASC' }}
        // @ts-ignore
        perPage={null}
        alwaysOn
      >
        <SelectArrayInput label="Combustíveis" optionText="name" />
      </ReferenceArrayInput>
    </Filter>
  );
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
      <ReferenceField source="scoringId" reference="scorings" label="Regra" textAlign="center" emptyText="--">
        <TextField source="identify" />
      </ReferenceField>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
};

export default props => {
  const filterDefaultValues = {
    chainId: getChainId(),
    from: moment().startOf('day').toISOString(),
    to: moment().endOf('day').toISOString()
  };

  return (
    <Permission permission={PERMISSIONS.SCORING_REPORT}>
      <RequiredChain>
        <ReportList periodRequired
          {...props}
          title="Relatório de Pontuação"
          resource="chains/chainId/reports/scoring"
          filters={<ListFilters />}
          filter={getPlaceId() ? { placeId: getPlaceId() } : null}
          perPage={25}
          filterDefaultValues={filterDefaultValues}
          pagination={<ListPagination />}
          bulkActionButtons={false}
          titleOnPrint="Relatório de Regras de Pontuação"
          filterTablePrint={<FilterTablePrint />}
          landscape
          showPagination
        >
          <ScoringReportContent />
        </ReportList>
      </RequiredChain>
    </Permission>
  );
};
